/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Home, MessageCircle, Users, Zap, Globe,
  Compass, Brain, User, LogOut, Sun, Moon,
  Search, Settings, Bell, Phone, BookOpen,
  Cloud, Image, FileText, Star, ChevronRight,
  Plus, X, Check, Wifi, WifiOff
} from 'lucide-react';
import { useAuth }   from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { useTheme }  from '../../context/ThemeContext';
import { getAvatarUrl } from '../../utils/helpers';
import GlobalSearch  from '../Search/GlobalSearch';
import './MainLayout.css';

const NAV_ITEMS = [
  { path:'/home',        icon:Home,          label:'Home',        key:'home'        },
  { path:'/chat',        icon:MessageCircle, label:'Messages',    key:'chat',  badge:true },
  { path:'/communities', icon:Users,         label:'Communities', key:'communities' },
  { path:'/feed',        icon:Globe,         label:'Feed',        key:'feed'        },
  { path:'/stories',     icon:Star,          label:'Stories',     key:'stories'     },
  { path:'/calls',       icon:Phone,         label:'Calls',       key:'calls'       },
  { path:'/ai',          icon:Brain,         label:'AI Assistant',key:'ai', special:true },
  { path:'/documents',   icon:FileText,      label:'Documents',   key:'documents'   },
  { path:'/media',       icon:Image,         label:'Media',       key:'media'       },
  { path:'/explore',     icon:Compass,       label:'Explore',     key:'explore'     },
  { path:'/groups',      icon:Zap,           label:'Groups',      key:'groups'      },
];

const NAV_BOTTOM = [
  { path:'/notifications', icon:Bell,     label:'Notifications', key:'notifications', badge:true },
  { path:'/settings',      icon:Settings, label:'Settings',      key:'settings'      },
];

export default function MainLayout() {
  const { user, logout }         = useAuth();
  const { notifications, isOnline, connected } = useSocket();
  const { toggleTheme, isDark }  = useTheme();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [showSearch,   setShowSearch]   = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifs,   setShowNotifs]   = useState(false);
  const [sidebarOpen,  setSidebarOpen]  = useState(false);
  // 'connected' now comes directly from SocketContext above — no more
  // duplicate/racy local tracking via getSocket().on(...)

  const activeKey = [...NAV_ITEMS, ...NAV_BOTTOM].find(n => location.pathname.startsWith(n.path))?.key;
  const unreadDMs = notifications.filter(n => n.type === 'dm').length;
  const unreadNotifs = notifications.length;

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey||e.metaKey) && e.key==='k') { e.preventDefault(); setShowSearch(v=>!v); }
      if (e.key==='Escape') { setShowSearch(false); setShowUserMenu(false); setShowNotifs(false); setSidebarOpen(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);


  const go = (path) => { navigate(path); setSidebarOpen(false); };

  return (
    <div className="app-shell">

      {/* ── Mobile overlay ───────────────────────────────── */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}/>}

      {/* ── LEFT SIDEBAR ─────────────────────────────────── */}
      <aside className={`app-sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-logo" onClick={() => go('/home')}>
          <div className="sidebar-logo-icon">⚡</div>
          <span className="sidebar-logo-text">Nexa</span>
        </div>

        {/* Nav items */}
        <nav className="sidebar-nav">
          {NAV_ITEMS.map(({ path, icon:Icon, label, key, special, badge }) => (
            <button
              key={key}
              className={`sidebar-item ${activeKey===key?'active':''} ${special?'special':''}`}
              onClick={() => go(path)}
              title={label}
            >
              <div className="sidebar-item-icon">
                <Icon size={20}/>
                {badge && key==='chat' && unreadDMs>0 && (
                  <span className="sidebar-badge">{unreadDMs>9?'9+':unreadDMs}</span>
                )}
              </div>
              <span className="sidebar-item-label">{label}</span>
              {activeKey===key && <div className="sidebar-active-bar"/>}
            </button>
          ))}
        </nav>

        {/* Bottom nav */}
        <div className="sidebar-bottom">
          <div className="sidebar-divider"/>
          {NAV_BOTTOM.map(({ path, icon:Icon, label, key, badge }) => (
            <button
              key={key}
              className={`sidebar-item ${activeKey===key?'active':''}`}
              onClick={() => go(path)}
              title={label}
            >
              <div className="sidebar-item-icon">
                <Icon size={20}/>
                {badge && unreadNotifs>0 && (
                  <span className="sidebar-badge">{unreadNotifs>9?'9+':unreadNotifs}</span>
                )}
              </div>
              <span className="sidebar-item-label">{label}</span>
            </button>
          ))}

          {/* Theme toggle */}
          <button className="sidebar-item" onClick={toggleTheme} title={isDark?'Light mode':'Dark mode'}>
            <div className="sidebar-item-icon">
              {isDark ? <Sun size={20}/> : <Moon size={20}/>}
            </div>
            <span className="sidebar-item-label">{isDark?'Light mode':'Dark mode'}</span>
          </button>

          {/* Profile */}
          <div className="sidebar-profile" onClick={() => setShowUserMenu(v=>!v)}>
            <div className="sidebar-avatar-wrap">
              <img src={getAvatarUrl(user)} alt="" className="sidebar-avatar"/>
              <span className={`sidebar-status-dot ${connected?'online':'offline'}`}/>
            </div>
            <div className="sidebar-profile-info">
              <p className="sidebar-profile-name">{user?.displayName}</p>
              <p className="sidebar-profile-handle">@{user?.username}</p>
            </div>
            <ChevronRight size={14} style={{color:'var(--text-500)',marginLeft:'auto',flexShrink:0}}/>
          </div>
        </div>

        {/* User menu */}
        {showUserMenu && (
          <>
            <div className="user-popup">
              <div className="user-popup-header">
                <img src={getAvatarUrl(user)} alt="" className="avatar" style={{width:44,height:44}}/>
                <div>
                  <p style={{fontWeight:700,fontSize:'.9rem'}}>{user?.displayName}</p>
                  <p style={{fontSize:'.75rem',color:'var(--text-400)'}}>@{user?.username}</p>
                </div>
                <div className="user-status-badge">
                  {connected ? <><Wifi size={11}/> Online</> : <><WifiOff size={11}/> Offline</>}
                </div>
              </div>
              <div className="user-popup-divider"/>
              {[
                { icon:User,     label:'View Profile',  action:()=>{go('/profile');setShowUserMenu(false);} },
                { icon:Settings, label:'Settings',      action:()=>{go('/settings');setShowUserMenu(false);} },
                { icon:isDark?Sun:Moon, label:isDark?'Light Mode':'Dark Mode', action:()=>{toggleTheme();setShowUserMenu(false);} },
              ].map(item => (
                <button key={item.label} className="user-popup-item" onClick={item.action}>
                  <item.icon size={16}/> {item.label}
                </button>
              ))}
              <div className="user-popup-divider"/>
              <button className="user-popup-item danger" onClick={()=>{logout();setShowUserMenu(false);}}>
                <LogOut size={16}/> Sign out
              </button>
            </div>
            <div style={{position:'fixed',inset:0,zIndex:149}} onClick={()=>setShowUserMenu(false)}/>
          </>
        )}
      </aside>

      {/* ── RIGHT PANEL ──────────────────────────────────── */}
      <div className="app-right">

        {/* ── TOP NAVBAR ─────────────────────────────────── */}
        <header className="app-topbar">
          {/* Mobile: hamburger */}
          <button className="topbar-hamburger" onClick={() => setSidebarOpen(v=>!v)}>
            <span/><span/><span/>
          </button>

          {/* Page title */}
          <div className="topbar-title">
            {[...NAV_ITEMS,...NAV_BOTTOM].find(n=>n.key===activeKey)?.label || 'Nexa'}
          </div>

          <div className="topbar-right">
            {/* Connection indicator */}
            {!connected && (
              <div className="topbar-offline">
                <WifiOff size={13}/> Offline
              </div>
            )}

            {/* Search */}
            <button className="topbar-btn" onClick={() => setShowSearch(true)} title="Search (Ctrl+K)">
              <Search size={18}/>
              <span className="topbar-btn-label">Search</span>
              <kbd className="topbar-kbd">⌘K</kbd>
            </button>

            {/* Notifications */}
            <div style={{position:'relative'}}>
              <button className="topbar-btn topbar-icon-btn" onClick={() => setShowNotifs(v=>!v)}>
                <Bell size={18}/>
                {unreadNotifs>0 && <span className="topbar-badge">{unreadNotifs>9?'9+':unreadNotifs}</span>}
              </button>
              {showNotifs && (
                <>
                  <div className="notif-popup">
                    <div className="notif-popup-header">
                      <h3>Notifications</h3>
                      {unreadNotifs>0 && (
                        <span className="notif-count">{unreadNotifs} new</span>
                      )}
                    </div>
                    {notifications.length===0 ? (
                      <div style={{padding:'32px',textAlign:'center',color:'var(--text-500)',fontSize:'.85rem'}}>
                        🎉 All caught up!
                      </div>
                    ) : notifications.slice(0,8).map(n => (
                      <div key={n.id} className="notif-item" onClick={() => {go('/chat');setShowNotifs(false);}}>
                        <div className="notif-icon">💬</div>
                        <div className="notif-content">
                          <p className="notif-title">{n.senderInfo?.displayName || 'New message'}</p>
                          <p className="notif-body">{n.preview || 'Sent you a message'}</p>
                        </div>
                        <div className="notif-dot"/>
                      </div>
                    ))}
                  </div>
                  <div style={{position:'fixed',inset:0,zIndex:149}} onClick={()=>setShowNotifs(false)}/>
                </>
              )}
            </div>

            {/* Quick action: new message */}
            <button className="topbar-btn topbar-icon-btn" onClick={() => go('/chat')} title="New Message">
              <Plus size={18}/>
            </button>

            {/* Avatar */}
            <div className="topbar-avatar-wrap" onClick={() => setShowUserMenu(v=>!v)}>
              <img src={getAvatarUrl(user)} alt="" className="topbar-avatar"/>
              <span className={`topbar-status ${connected?'online':'offline'}`}/>
            </div>
          </div>
        </header>

        {/* ── MAIN CONTENT ──────────────────────────────── */}
        <main className="app-content">
          <Outlet/>
        </main>
      </div>

      {/* ── Global search ────────────────────────────────── */}
      {showSearch && <GlobalSearch onClose={() => setShowSearch(false)}/>}
    </div>
  );
}